'use client';

import { useCallback, useSyncExternalStore } from 'react';

/**
 * SSR 安全的 matchMedia hook（基于 useSyncExternalStore，无 setState-in-effect）。
 *
 * - 服务端与首帧 hydration 用 getServerSnapshot 返回 defaultValue（不读 window，避免 hydration mismatch）；
 *   hydration 后切换为真实 matches。
 * - 仅用于驱动「CSS 做不了」的分支（如承载层在 popover / sheet 间切换）。
 *   尺寸 / 堆叠 / 间距等一律走 Tailwind 响应式前缀，不要用本 hook。
 */
export function useMediaQuery(query: string, defaultValue = false): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onStoreChange);
      return () => mql.removeEventListener('change', onStoreChange);
    },
    [query]
  );

  const getSnapshot = useCallback(() => window.matchMedia(query).matches, [query]);

  const getServerSnapshot = useCallback(() => defaultValue, [defaultValue]);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * 桌面端判定（>= md 768px）。
 * desktop-first：defaultValue=true，SSR 与首帧一致；移动端 hydration 后翻转为 false。
 * 弹层内容仅在 open 时渲染，故翻转不可见、无 hydration mismatch。
 */
export const useIsDesktop = () => useMediaQuery('(min-width: 768px)', true);
